import axios from 'axios';
import { apiClient } from '@lib/api/client';
import { MESSAGES } from '@lib/constants/messages';
import type {
  ProductResponse,
  CurrentSubscription,
  UpgradeDowngradeRequest,
  AvailablePlansResponse,
  CardInfo,
  ChangePlanResponse,
  ConsumptionResponse,
  CompanyConsumption,
  ValidationResult,
  PlanLimits
} from '../types/subscription.types';

const SUBSCRIPTION_API_URL = import.meta.env.VITE_SUBSCRIPTION_API_BASE_URL || 'https://subs.hypernovalabs.com/api/v1';
const PRODUCT_SLUG = import.meta.env.VITE_SUBSCRIPTION_PRODUCT || 'factura-libre';

export const subscriptionService = {
  /**
   * Get available plans with current subscription info
   * This uses the BFF endpoint which handles token management
   */
  async getAvailablePlans(): Promise<AvailablePlansResponse> {
    try {
      const response = await apiClient.get<AvailablePlansResponse>('/companies/available-plans');
      return response.data;
    } catch (error) {
      console.error('Error fetching available plans:', error);
      throw error;
    }
  },

  /**
   * Get product info (legacy endpoint - kept for compatibility)
   */
  async getProductInfo(): Promise<ProductResponse> {
    try {
      const response = await axios.get<ProductResponse>(
        `${SUBSCRIPTION_API_URL}/products/slug/${PRODUCT_SLUG}`,
        {
          headers: {
            'accept': 'application/json',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching product info:', error);
      throw error;
    }
  },

  async getCurrentSubscription(): Promise<CurrentSubscription> {
    try {
      const response = await apiClient.get<CurrentSubscription>('/companies/current-subscription');
      return response.data;
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      throw error;
    }
  },

  async upgradePlan(_request: UpgradeDowngradeRequest): Promise<{ success: boolean; message: string }> {
    throw new Error('Cambio de plan sin pago no está disponible. Por favor, usa el flujo de pago.');
  },

  async downgradePlan(_request: UpgradeDowngradeRequest): Promise<{ success: boolean; message: string }> {
    throw new Error('El downgrade de plan no está disponible actualmente. Contacta soporte.');
  },

  async cancelSubscription(): Promise<{ success: boolean; message: string }> {
    throw new Error('La cancelación de suscripción no está disponible actualmente. Contacta soporte.');
  },

  /**
   * Change plan with payment
   * Handles the complete flow: change plan -> process payment
   */
  async changePlanWithPayment(
    subscriptionId: string,
    newPlanId: string,
    cardInfo: CardInfo,
    amount: number,
    customerName?: string,
    customerPhone?: string,
    customerAddress?: string
  ): Promise<ChangePlanResponse & { payment_result: any }> {
    try {
      // Generate idempotency key to prevent duplicate charges
      const idempotencyKey = crypto.randomUUID();

      const response = await apiClient.post('/companies/change-plan-with-payment', {
        subscriptionId,
        newPlanId,
        cardInfo,
        amount,
        customerName,
        customerPhone,
        customerAddress,
        idempotencyKey,
      });

      return response.data;
    } catch (error) {
      console.error('Error changing plan with payment:', error);
      throw error;
    }
  },

  /**
   * Get company consumption (invoices, users, etc.)
   * This will call the Aludra API through the BFF
   */
  async getCompanyConsumption(companyId: string, year?: number, month?: number): Promise<CompanyConsumption> {
    try {
      const currentDate = new Date();
      const params = {
        year: year || currentDate.getFullYear(),
        month: month || currentDate.getMonth() + 1,
      };

      const response = await apiClient.get<ConsumptionResponse>(
        `/companies/${companyId}/consumption`,
        { params }
      );

      if (response.data.data && response.data.data.length > 0) {
        return response.data.data[0];
      }

      throw new Error(MESSAGES.NO_CONSUMPTION_DATA);
    } catch (error) {
      console.error('Error fetching company consumption:', error);
      throw error;
    }
  },

  /**
   * Validate if user can add more users based on plan limits
   */
  validateCanAddUser(consumption: CompanyConsumption, planLimits: PlanLimits): ValidationResult {
    const currentUsers = consumption.UserQty;
    const maxUsers = planLimits.usuarios;

    console.log('🔍 validateCanAddUser:', {
      currentUsers,
      maxUsers,
      consumption,
      planLimits,
    });

    // -1 means unlimited
    if (maxUsers === -1) {
      console.log('✅ Usuarios ilimitados');
      return {
        allowed: true,
      };
    }

    if (currentUsers >= maxUsers) {
      console.log('❌ Límite de usuarios alcanzado:', currentUsers, '>=', maxUsers);
      return {
        allowed: false,
        reason: `Has alcanzado el límite de ${maxUsers} usuarios de tu plan actual.`,
        currentCount: currentUsers,
        limit: maxUsers,
        suggestedPlan: consumption.SuggestedPlan,
      };
    }

    console.log('✅ Puede agregar usuario:', currentUsers, '<', maxUsers);
    return {
      allowed: true,
      currentCount: currentUsers,
      limit: maxUsers,
    };
  },

  /**
   * Validate if user can add more invoices based on plan limits
   */
  validateCanAddInvoice(consumption: CompanyConsumption, planLimits: PlanLimits, isMonthly: boolean = true): ValidationResult {
    const currentInvoices = isMonthly ? consumption.MonthlyInvoiceQty : consumption.AnnualInvoiceQty;
    const maxInvoices = isMonthly ? planLimits.facturas_mes : planLimits.facturas_annual;

    // -1 or undefined means unlimited
    if (!maxInvoices || maxInvoices === -1) {
      return {
        allowed: true,
      };
    }

    if (currentInvoices >= maxInvoices) {
      const period = isMonthly ? 'mes' : 'año';
      return {
        allowed: false,
        reason: `Has alcanzado el límite de ${maxInvoices} facturas por ${period} de tu plan actual.`,
        currentCount: currentInvoices,
        limit: maxInvoices,
        suggestedPlan: consumption.SuggestedPlan,
      };
    }

    return {
      allowed: true,
      currentCount: currentInvoices,
      limit: maxInvoices,
    };
  },

  /**
   * Check if user can modify templates based on plan
   */
  canModifyTemplate(planLimits: PlanLimits, templateType: 'cafe' | 'email'): boolean {
    if (templateType === 'cafe') {
      return planLimits.modify_template_cafe;
    }
    return planLimits.modify_template_email;
  },

  /**
   * Check if user can modify logo based on plan
   */
  canModifyLogo(planLimits: PlanLimits): boolean {
    return planLimits.modify_logo;
  },

  /**
   * Validate if user can add more branch offices based on plan limits
   */
  validateCanAddBranchOffice(consumption: CompanyConsumption, planLimits: PlanLimits): ValidationResult {
    const currentBranchOffices = consumption.BranchOfficeQty;
    const maxBranchOffices = planLimits.branch_office;

    // -1 means unlimited
    if (maxBranchOffices === -1) {
      return {
        allowed: true,
      };
    }

    if (currentBranchOffices >= maxBranchOffices) {
      return {
        allowed: false,
        reason: `Has alcanzado el límite de ${maxBranchOffices} sucursales de tu plan actual.`,
        currentCount: currentBranchOffices,
        limit: maxBranchOffices,
        suggestedPlan: consumption.SuggestedPlan,
      };
    }

    return {
      allowed: true,
      currentCount: currentBranchOffices,
      limit: maxBranchOffices,
    };
  },

  /**
   * Validate if user can add more products based on plan limits
   */
  validateCanAddProduct(consumption: CompanyConsumption, planLimits: PlanLimits): ValidationResult {
    const currentProducts = consumption.ProductQty;
    const maxProducts = planLimits.products;

    // -1 means unlimited
    if (maxProducts === -1) {
      return {
        allowed: true,
      };
    }

    if (currentProducts >= maxProducts) {
      return {
        allowed: false,
        reason: `Has alcanzado el límite de ${maxProducts} productos de tu plan actual.`,
        currentCount: currentProducts,
        limit: maxProducts,
        suggestedPlan: consumption.SuggestedPlan,
      };
    }

    return {
      allowed: true,
      currentCount: currentProducts,
      limit: maxProducts,
    };
  },

  /**
   * Validate if user can add more customers based on plan limits
   */
  validateCanAddCustomer(consumption: CompanyConsumption, planLimits: PlanLimits): ValidationResult {
    const currentCustomers = consumption.CustomerQty;
    const maxCustomers = planLimits.customers;

    // -1 means unlimited
    if (maxCustomers === -1) {
      return {
        allowed: true,
      };
    }

    if (currentCustomers >= maxCustomers) {
      return {
        allowed: false,
        reason: `Has alcanzado el límite de ${maxCustomers} clientes de tu plan actual.`,
        currentCount: currentCustomers,
        limit: maxCustomers,
        suggestedPlan: consumption.SuggestedPlan,
      };
    }

    return {
      allowed: true,
      currentCount: currentCustomers,
      limit: maxCustomers,
    };
  },
};
